import { useQueryClient } from '@tanstack/react-query';
import { getRouteApi, useNavigate } from '@tanstack/react-router';
import { useEffect, useState, type ReactElement } from 'react';
import { Link } from 'react-aria-components';
import { formatDate, formatRelativeTime, type ReportSummary, PRODUCT_NAME } from '@tallyframe/core';
import { useMe, useOnboarding } from '../api/onboarding-queries';
import { queryKeys, useReports } from '../api/queries';
import { ApiError } from '../api/errors';
import type { AppConfig } from '../api/types';
import { useSession } from '../app/session-context';
import { upgradeHref } from '../components/PlanLimitMessage';
import { useBrand } from '../brandContext';
import { Checklist } from '../onboarding/Checklist';
import { useDismissal } from '../onboarding/dismissal';
import { SampleChooser } from '../onboarding/SampleChooser';
import { checklistView, stepsDone } from '../onboarding/view';
import { CsvUploadDialog } from '../reports/CsvUploadDialog';
import { GoogleErrorBanner } from '../sources/connect';
import { SheetCreateDialog } from '../sources/SheetCreateDialog';
import { useExport } from '../reports/useExport';
import { Badge } from '../ui/Badge';
import { Banner } from '../ui/Banner';
import { Button, ButtonLink } from '../ui/Button';
import { Card } from '../ui/Card';
import { CopyButton } from '../ui/CopyButton';
import { Dialog } from '../ui/Dialog';
import { PageHeader } from '../ui/PageHeader';
import { Stack } from '../ui/Stack';
import { StatusState } from '../ui/StatusState';
import { Table, type TableColumn } from '../ui/Table';
import { useDocumentTitle } from '../useDocumentTitle';

const SEED_COMMAND = 'pnpm seed:demo';
const LINK =
  'text-ink font-semibold underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink';
const MUTED_TEXT = 'm-0 p-0 font-ui text-sm text-muted';
const INLINE_CODE = 'font-mono text-sm bg-desk border border-solid border-line rounded-control px-1 break-words';

const reportsIndexApi = getRouteApi('/w/$workspaceId/reports/');

/** The Export cell of one row. Its own component: `useExport` is a hook, and each row needs its own status. */
function ExportCell({ report }: { report: ReportSummary }): ReactElement {
  const { status, exportPdf } = useExport(report.id);
  const working = status.kind === 'working';
  return (
    <div className="m-0 p-0 border-0 bg-transparent flex flex-col items-start gap-1">
      <Button
        variant="quiet"
        size="sm"
        aria-label={`${working ? 'Exporting PDF' : 'Export PDF'} for ${report.title}`}
        isDisabled={working}
        onPress={() => void exportPdf()}
      >
        {working ? 'Exporting…' : 'Export PDF'}
      </Button>
      {status.kind === 'error' ? (
        <p role="alert" className="m-0 p-0 font-ui text-sm text-danger whitespace-normal">
          {status.message}
        </p>
      ) : null}
    </div>
  );
}

function ReportTitleCell({ report, workspaceId }: { report: ReportSummary; workspaceId: string }): ReactElement {
  return (
    <span className="m-0 p-0 border-0 bg-transparent inline-flex items-center gap-2">
      <Link href={`/w/${encodeURIComponent(workspaceId)}/reports/${encodeURIComponent(report.id)}`} className={LINK}>
        {report.title}
      </Link>
      {report.sample ? <Badge tone="neutral">Sample</Badge> : null}
      {report.publishedUrl ? (
        <svg
          role="img"
          aria-label="Published"
          className="text-muted"
          width={14}
          height={14}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={1.5}
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <title>Published</title>
          <path d="M10 14a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7l-1 1" />
          <path d="M14 10a4 4 0 0 0-5.7 0l-3 3a4 4 0 0 0 5.7 5.7l1-1" />
        </svg>
      ) : null}
    </span>
  );
}

function columnsFor(workspaceId: string): TableColumn<ReportSummary>[] {
  return [
    {
      id: 'title',
      label: 'Report',
      isRowHeader: true,
      render: (report) => <ReportTitleCell report={report} workspaceId={workspaceId} />
    },
    {
      id: 'dataAsOf',
      label: 'Data as of',
      render: (report) => (report.dataAsOf ? formatDate(report.dataAsOf) : 'No data yet')
    },
    { id: 'updatedAt', label: 'Last edited', render: (report) => formatRelativeTime(report.updatedAt) },
    // Phones view and share; exporting a PDF from the list is a desk task.
    { id: 'export', label: 'Export', hideOnPhone: true, render: (report) => <ExportCell report={report} /> }
  ];
}

/**
 * Reports home (spec §9.2).
 *
 * One list, three data columns — Report, Data as of, Last edited — and a row
 * export action. No revision number, no snapshot wording. `?create=csv` opens
 * the upload flow and `?create=new` the "New report" chooser, so the command
 * palette can open either from any page.
 */
export function HomeRoute(props: { config?: AppConfig | null } = {}): ReactElement {
  useDocumentTitle('Reports');

  const config = props.config ?? null;
  const { snapshot } = useSession();
  const workspaceId = snapshot.workspaceId ?? '';
  const { create, google_error: googleError } = reportsIndexApi.useSearch();
  const navigate = useNavigate();
  const { brand } = useBrand();
  // `null` means "not loaded yet": the prompt only appears once the brand is known to be unset.
  const brandUnset = brand !== null && brand.displayName === '' && !brand.hasLogo;
  const reports = useReports(workspaceId);
  const queryClient = useQueryClient();
  const refetchReports = reports.refetch;
  // The list is cached for 30 s, but "Last edited" and the Published icon must be
  // current whenever this page opens: a warm cache is refreshed on mount (a cold
  // one is already being fetched).
  useEffect(() => {
    if (queryClient.getQueryData(queryKeys.reports(workspaceId)) !== undefined) void refetchReports();
    // Mount and workspace change only: a refetch on every render would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryClient, workspaceId]);

  const setCreate = (next: 'csv' | 'new' | 'sheet' | undefined): void => {
    void navigate({
      to: '/w/$workspaceId/reports',
      params: { workspaceId },
      search: { create: next },
      // Opening pushes (Back returns to the chooser); closing replaces, so Back does not reopen the dialog.
      replace: next === undefined
    });
  };
  const goToReport = (detail: { report: { id: string } }): void => {
    void navigate({
      to: '/w/$workspaceId/reports/$reportId',
      params: { workspaceId, reportId: detail.report.id },
      search: {}
    });
  };
  const wsPath = encodeURIComponent(workspaceId);

  // `import.meta.env.DEV` is a build-time constant, so this branch is removed from a production bundle.
  const showSeedHint = import.meta.env.DEV && config !== null && !config.demoSeeded;
  const list = reports.data?.reports ?? null;
  const error = reports.error;

  const onboarding = useOnboarding(workspaceId);
  const me = useMe();
  const dismissal = useDismissal(me.data?.id ?? null, workspaceId);
  const [sampleOpen, setSampleOpen] = useState(false);
  // A failed load is the spec's fallback (today's empty state), never an error banner.
  const failed = onboarding.error ?? me.error;
  const pending = !failed && (onboarding.isPending || me.isPending);
  const onboardingData = onboarding.data;
  const view =
    failed || pending || !onboardingData || list === null
      ? 'none'
      : checklistView({
          stepsDone: stepsDone(list, onboardingData),
          agentCardOpen: !onboardingData.agentConnected && !dismissal.agent,
          dismissed: dismissal.checklist,
          reopened: dismissal.reopened
        });
  useEffect(() => {
    if (failed) console.warn('onboarding load failed', failed);
  }, [failed]);
  const checklist =
    view !== 'none' && onboardingData && list !== null ? (
      <Checklist
        workspaceId={workspaceId}
        userId={me.data?.id ?? null}
        reports={list}
        onboarding={onboardingData}
        view={view}
        agentCardOpen={!onboardingData.agentConnected && !dismissal.agent}
        compact={list.length > 0}
        onTrySample={() => setSampleOpen(true)}
        onUpload={() => setCreate('csv')}
        onSheet={config?.googleSheets ? () => setCreate('sheet') : undefined}
      />
    ) : null;

  return (
    <div className="p-6">
      <Stack gap={4}>
        <PageHeader
          title="Reports"
          actions={
            <>
              <ButtonLink href={`/w/${wsPath}/agents`}>Connect an agent</ButtonLink>
              {/* Like Upload CSV: an empty workspace's checklist carries the tile, so the header does not repeat it. */}
              {list !== null && config?.googleSheets && (list.length > 0 || !checklist) ? (
                <Button onPress={() => setCreate('sheet')}>From a Google Sheet</Button>
              ) : null}
              {list !== null && list.length > 0 ? (
                <Button variant="primary" onPress={() => setCreate('csv')}>
                  Upload CSV
                </Button>
              ) : null}
            </>
          }
        />
        <GoogleErrorBanner code={googleError} />
        {brandUnset ? (
          <p className={MUTED_TEXT}>
            <ButtonLink variant="quiet" size="sm" href={`/w/${wsPath}/settings/brand`}>
              Add your logo and color so reports carry your brand
            </ButtonLink>
          </p>
        ) : null}

        {showSeedHint ? (
          <Banner tone="info" title="Load demo data" actions={<CopyButton value={SEED_COMMAND} label="Copy command" />}>
            This workspace has no demo report yet. Run <code className={INLINE_CODE}>{SEED_COMMAND}</code> in the
            repository root to load the weekly-labor example, then reload this page. Development builds only.
          </Banner>
        ) : null}

        {error ? (
          <StatusState
            kind="error"
            title="Reports could not be loaded"
            message={ApiError.isApiError(error) ? error.message : 'Try again.'}
            action={
              <Button size="sm" onPress={() => void reports.refetch()}>
                Try again
              </Button>
            }
          />
        ) : null}

        {(list === null && !error) || (list !== null && list.length === 0 && pending) ? <StatusState kind="loading" title="Loading reports…" /> : null}

        {list !== null && list.length === 0 && !pending && checklist ? checklist : null}

        {list !== null && list.length === 0 && !pending && !checklist ? (
          <Stack gap={4}>
            <StatusState kind="empty" title="No reports yet" message="There are two ways in." />
            <div className="m-0 p-0 border-0 bg-transparent grid grid-cols-2 gap-4">
              <Card title="Upload a CSV">
                <Stack gap={3}>
                  <p className={MUTED_TEXT}>
                    Pick a file, check the columns {PRODUCT_NAME} inferred, correct anything wrong, and get a starter
                    report you can edit.
                  </p>
                  <div className="m-0 p-0 border-0 bg-transparent">
                    <Button onPress={() => setCreate('csv')}>Upload CSV</Button>
                  </div>
                </Stack>
              </Card>
              <Card title="Connect an agent">
                <Stack gap={3}>
                  <p className={MUTED_TEXT}>
                    Point your own agent at the MCP endpoint and let it create the dataset, send the rows and keep them
                    up to date. You still own the report layout.
                  </p>
                  <div className="m-0 p-0 border-0 bg-transparent">
                    <ButtonLink href={`/w/${wsPath}/agents`}>Connect an agent</ButtonLink>
                  </div>
                </Stack>
              </Card>
            </div>
          </Stack>
        ) : null}

        {list !== null && list.length > 0 && !pending ? checklist : null}

        {list !== null && list.length > 0 ? (
          <div
            data-testid="reports-table"
            className="m-0 p-0 border border-solid border-line rounded-panel bg-surface overflow-hidden"
          >
            <Table label="Reports in this workspace" columns={columnsFor(workspaceId)} rows={list} />
          </div>
        ) : null}

        <Dialog
          title="New report"
          size="sm"
          isOpen={create === 'new'}
          onOpenChange={(open) => {
            if (!open) setCreate(undefined);
          }}
          footer={<Button onPress={() => setCreate(undefined)}>Cancel</Button>}
        >
          <Stack gap={3}>
            <Button variant="primary" onPress={() => setCreate('csv')}>
              Upload a CSV
            </Button>
            <ButtonLink href={`/w/${wsPath}/agents`}>Connect an agent</ButtonLink>
            {config?.googleSheets ? <Button onPress={() => setCreate('sheet')}>From a Google Sheet</Button> : null}
          </Stack>
        </Dialog>

        <SampleChooser isOpen={sampleOpen} onOpenChange={setSampleOpen} added={onboardingData?.samples ?? []} />

        {create === 'csv' ? (
          <CsvUploadDialog upgradeUrl={upgradeHref(config?.upgradeUrl ?? null, snapshot)} onClose={() => setCreate(undefined)} onCreated={goToReport} />
        ) : null}
        {create === 'sheet' ? <SheetCreateDialog onClose={() => setCreate(undefined)} onCreated={goToReport} /> : null}
      </Stack>
    </div>
  );
}
